import type { Row } from "../api";
import { fieldTargetId } from "../field-validation";
import {
  parseFilingInterview,
  deriveFilingInterview,
  filingReviewToken,
  type FilingInterview,
} from "../filing-status";
import {
  filingLabels,
  filingStatusLabels,
  filingVisibleFields,
  filingStatusFieldIssues,
  maritalSituationLabels,
  filingHouseholdSummary,
  invalidSavedFilingInterview,
  isNonresidentHohPath,
  type FilingField,
} from "../filing-status-presentation";

interface Props {
  value: unknown;
  selected: Row;
  previousRaw: unknown;
  rootId: string;
  disabled?: boolean;
  visibleFields?: FilingField[];
  pageTitle?: string;
  showHouseholdSummary?: boolean;
  builderMode?: boolean;
  showRequiredErrors?: boolean;
  onChange: (value: FilingInterview) => void;
  onDependents: () => void;
}
const yesNo: [string, string][] = [
  ["YES", "Yes"],
  ["NO", "No"],
  ["UNSURE", "Not sure"],
];
const spouseFields: FilingField[] = [
  "spouseName",
  "spouseIdStatus",
  "spouseTaxId",
  "spouseResidency",
  "spouseResidentElection",
];
const householdFields: FilingField[] = [
  "paidMoreThanHalfHomeCosts",
  "qualifyingPersonName",
  "qualifyingPersonRelationship",
  "qualifyingPersonResidence",
  "qualifyingPersonClaim",
];
export function FilingStatusInterview({
  value,
  selected,
  previousRaw,
  rootId,
  disabled = false,
  visibleFields: requestedFields,
  pageTitle,
  showHouseholdSummary = true,
  builderMode = false,
  showRequiredErrors = true,
  onChange,
  onDependents,
}: Props) {
  const state = parseFilingInterview(value),
    status = state.filingStatus;
  const visible = filingVisibleFields(state, value).filter(
    (name) => !requestedFields || requestedFields.includes(name),
  );
  const analysis = deriveFilingInterview(state, selected, previousRaw);
  const conflictFields = (target: string): FilingField[] => {
    if (["SC-MARRIAGE", "taxpayer.marital_status_year_end"].includes(target))
      return ["maritalSituation"];
    if (target === "SC-SPOUSE-DEATH")
      return [
        state.maritalSituation === "WIDOWED"
          ? "spouseDeathYear"
          : state.maritalSituation === "NEVER_MARRIED"
            ? "maritalSituation"
            : "recentSpouseDeath",
      ];
    if (target === "SC-RDP-COMMUNITY") return ["communityIncome"];
    if (target === "SC-HOUSEHOLD") return householdFields;
    if (target === "Q-008" || target === "filingStatus")
      return ["filingStatus"];
    return [];
  };
  const conflictVisible = (target: string) =>
    !requestedFields ||
    conflictFields(target).some((name) => visible.includes(name));
  const issues = filingStatusFieldIssues(
    value,
    rootId,
    selected,
    previousRaw ?? null,
  )
    .map((issue) => {
      if (!requestedFields || !issue.id.startsWith(rootId + "-review-"))
        return issue;
      const field = conflictFields(
        issue.id.slice((rootId + "-review-").length),
      ).find((name) => visible.includes(name));
      return field
        ? {
            ...issue,
            path: [field],
            targetId: fieldTargetId(rootId, [field]),
            fieldLabel: filingLabels[field],
          }
        : issue;
    })
    .filter(
      (issue) =>
        (showRequiredErrors || issue.kind !== "required") &&
        visible.includes(issue.path[0] as FilingField) &&
        (!issue.id.startsWith(rootId + "-review-") ||
          conflictVisible(issue.id.slice((rootId + "-review-").length))),
    );
  const priorConflicts = analysis.conflicts.filter(
    (c) =>
      (c.target.startsWith("SC-") ||
        c.target === "Q-008" ||
        c.target === "taxpayer.marital_status_year_end") &&
      conflictVisible(c.target) &&
      (!requestedFields ||
        issues.some((issue) => issue.id === rootId + "-review-" + c.target)),
  );
  const unseenConflicts = Boolean(
    requestedFields &&
    analysis.conflicts.some((c) => !conflictVisible(c.target)),
  );
  if (requestedFields && visible.length === 0) return null;
  if (invalidSavedFilingInterview(value))
    return (
      <section
        className="tax-topic-alert"
        id={rootId + "-saved"}
        tabIndex={-1}
        aria-label="Saved filing-status information"
      >
        <strong>Filing Status — Saved filing-status information</strong>
        <p>
          Previously saved filing-status information cannot be edited safely in
          this form. Ask an administrator to review it; the original entry has
          been preserved.
        </p>
      </section>
    );
  const edit = (name: FilingField, next: string) => {
    const changed: FilingInterview = { ...state, version: 2 };
    delete changed.reviewedPrevious;
    if (next) Object.assign(changed, { [name]: next });
    else delete changed[name];
    onChange(changed);
  };
  const field = (
    name: FilingField,
    options?: [string, string][],
    help?: string,
  ) => {
    if (!visible.includes(name)) return null;
    const choices = builderMode
      ? options?.filter(
          ([choice]) => !["UNSURE", "UNKNOWN", "CONFLICT"].includes(choice),
        )
      : options;
    const unresolved =
      builderMode &&
      ["UNSURE", "UNKNOWN", "CONFLICT"].includes(String(state[name]));
    const id = fieldTargetId(rootId, [name]),
      local = issues.filter((i) => i.targetId === id);
    const common = {
      id,
      disabled,
      "aria-invalid": local.length > 0,
      "aria-describedby":
        [help ? id + "-help" : "", local.length ? id + "-error" : ""]
          .filter(Boolean)
          .join(" ") || undefined,
      style: local.length ? { borderColor: "#b42318" } : undefined,
    };
    return (
      <div
        className={
          "tax-field intake-value-field" +
          (local.length ? " has-field-error" : "")
        }
        key={name}
      >
        <label htmlFor={id}>{filingLabels[name]}</label>
        {choices ? (
          <select
            {...common}
            value={
              (name === "filingStatus" && status === "OTHER") || unresolved
                ? ""
                : (state[name] ?? "")
            }
            onChange={(event) => edit(name, event.target.value)}
          >
            <option value="">
              {builderMode
                ? "Choose…"
                : name === "filingStatus"
                  ? "Select filing status"
                  : "Select an answer"}
            </option>
            {choices.map(([v, label]) => (
              <option key={v} value={v}>
                {label}
              </option>
            ))}
          </select>
        ) : (
          <input
            {...common}
            type="text"
            autoComplete="off"
            maxLength={name === "spouseTaxId" ? 20 : 200}
            inputMode={name === "spouseTaxId" ? "numeric" : undefined}
            value={state[name] ?? ""}
            onChange={(event) => edit(name, event.target.value)}
          />
        )}
        {help && (
          <small id={id + "-help"}>
            {builderMode ? help.replace("This interview", "This form") : help}
          </small>
        )}
        {local.length > 0 && (
          <div id={id + "-error"}>
            {local.map((i) => (
              <p className="field-validation-error" key={i.id}>
                {i.sectionLabel} — {i.fieldLabel}: {i.reason}
              </p>
            ))}
          </div>
        )}
      </div>
    );
  };
  const realStatus = Boolean(status && status !== "OTHER");
  return (
    <section
      className="tax-topic-block filing-interview"
      aria-label={
        pageTitle ??
        (builderMode ? "Filing status details" : "Filing status interview")
      }
    >
      {!builderMode && (
        <>
          <h3>{pageTitle ?? "Choose the right filing status"}</h3>
          <p>
            {requestedFields
              ? "Complete the relevant information on this page. Earlier answers are retained when you move between topics."
              : "Select a filing status, then complete the relevant spouse or household details below. Your earlier answers are retained when you change the selection."}
          </p>
        </>
      )}
      {[
        "filingStatus",
        "maritalSituation",
        "spouseDeathYear",
        "partnership",
        "recentSpouseDeath",
      ].some((name) => visible.includes(name as FilingField)) && (
        <div className="tax-topic-fields">
          {field("filingStatus", Object.entries(filingStatusLabels))}
          {field(
            "maritalSituation",
            Object.entries(maritalSituationLabels),
            "Use legal marital status, not whether the taxpayer currently lives with a partner. Recognized common-law marriages count as marriages.",
          )}
          {field("spouseDeathYear", [
            ["2025", "2025"],
            ["2024", "2024"],
            ["2023", "2023"],
            ["EARLIER", "Before 2023"],
            ["UNSURE", "Not sure"],
          ])}
          {field(
            "partnership",
            yesNo,
            "This means a legally registered partnership or civil union, not simply sharing a home.",
          )}
          {field(
            "recentSpouseDeath",
            yesNo,
            "An earlier answer identified a possible spouse-death issue. Review that information without changing unrelated records.",
          )}
        </div>
      )}
      {status === "OTHER" && visible.includes("filingStatus") && (
        <div className="tax-topic-notice">
          <strong>Choose the exact filing status</strong>
          <p>
            Your selection is retained from the earlier form, but it did not
            identify a specific status. Choose one of the five filing statuses
            above; no status has been assumed.
          </p>
        </div>
      )}
      {spouseFields.some((name) => visible.includes(name)) && (
        <fieldset className="filing-interview-group">
          <legend>
            {status === "HOH"
              ? "Spouse residency and household treatment"
              : "Spouse information"}
          </legend>
          <div className="tax-topic-fields">
            {field("spouseName")}
            {field("spouseIdStatus", [
              ["HAS_ID", "Social Security number or ITIN available"],
              ["NO_US_TIN", "No U.S. tax ID available — review needed"],
              ["UNSURE", "Not sure"],
            ])}
            {field(
              "spouseTaxId",
              undefined,
              "Synthetic information only. Enter the number from the spouse’s records; never use a placeholder for an unavailable number.",
            )}
            {field("spouseResidency", [
              [
                "US_CITIZEN_OR_RESIDENT_ALL_YEAR",
                "U.S. citizen or resident alien all year",
              ],
              [
                "NONRESIDENT_OR_DUAL_STATUS",
                "Nonresident alien during some or all of 2025",
              ],
              ["UNSURE", "Not sure"],
            ])}
            {field(
              "spouseResidentElection",
              yesNo,
              "Use the applicable election records. Selecting this answer does not make or revoke an election.",
            )}
          </div>
          {visible.includes("spouseIdStatus") &&
            state.spouseIdStatus === "NO_US_TIN" && (
              <p className="tax-topic-notice">
                Keep the identity details for review. A nonresident-spouse
                exception or tax-ID application may apply; do not enter a
                fabricated number.
              </p>
            )}
          {visible.includes("spouseResidency") &&
            state.spouseResidency === "NONRESIDENT_OR_DUAL_STATUS" && (
              <p className="tax-topic-notice">
                {requestedFields && !visible.includes("spouseResidentElection")
                  ? "Review the spouse’s U.S. tax residency using the taxpayer’s records."
                  : builderMode
                    ? "Review the spouse’s residency and any resident-treatment election. Spouse residency affects the available filing treatment; it does not automatically decide it."
                    : "Review the nonresident-spouse rules and any resident-treatment election. Spouse residency affects the available filing treatment; it does not automatically decide it."}
              </p>
            )}
        </fieldset>
      )}
      {status === "MFJ" && visible.includes("jointReturnConsent") && (
        <fieldset className="filing-interview-group">
          <legend>Preparing a joint return</legend>
          <p>
            A joint return combines both spouses’ income and deductions. One
            spouse having no income does not prevent joint filing.
          </p>
          <div className="tax-topic-fields">
            {field(
              "jointReturnConsent",
              yesNo,
              "For a deceased or incapacitated spouse, review who may authorize and sign the return. This interview does not collect a legal signature.",
            )}
          </div>
        </fieldset>
      )}
      {status === "MFS" &&
        ["spouseItemizes", "livedWithSpouse"].some((name) =>
          visible.includes(name as FilingField),
        ) && (
          <fieldset className="filing-interview-group">
            <legend>Preparing separate returns</legend>
            <div className="tax-topic-fields">
              {field("spouseItemizes", yesNo)}
              {field("livedWithSpouse", yesNo)}
            </div>
            {visible.includes("spouseItemizes") &&
              state.spouseItemizes === "YES" && (
                <p className="tax-topic-notice">
                  The spouse’s itemized deductions affect this taxpayer’s
                  standard-deduction eligibility. Retain the spouse’s deduction
                  information for the deduction review.
                </p>
              )}
          </fieldset>
        )}
      {visible.includes("communityIncome") && (
        <fieldset className="filing-interview-group">
          <legend>Shared income and property</legend>
          <div className="tax-topic-fields">
            {field(
              "communityIncome",
              yesNo,
              "Review the states of residence and ownership of income or property. Community-property treatment depends on the actual circumstances, not just the mailing address.",
            )}
          </div>
          {((visible.includes("partnership") && state.partnership === "YES") ||
            state.communityIncome === "YES") && (
            <p className="tax-topic-notice">
              Retain the relationship and allocation records for review. A
              registered partnership does not, by itself, prevent Single filing
              status.
            </p>
          )}
        </fieldset>
      )}
      {(visible.includes("spouseAbsentLastSixMonths") ||
        visible.includes("separateReturn")) && (
        <fieldset className="filing-interview-group">
          <legend>Head of Household while married and living apart</legend>
          <div className="tax-topic-fields">
            {field(
              "spouseAbsentLastSixMonths",
              yesNo,
              "The spouse must not live in the home during any of July–December. Temporary absences count as living in the home.",
            )}
            {field("separateReturn", yesNo)}
          </div>
        </fieldset>
      )}
      {isNonresidentHohPath(state) &&
        visible.includes("spouseResidency") &&
        visible.includes("paidMoreThanHalfHomeCosts") && (
          <p className="tax-topic-notice">
            Review the nonresident-spouse Head of Household route. The spouse is
            not the qualifying person; another qualifying person and the
            household-cost requirements still apply. This route does not require
            the six-month separation test.
          </p>
        )}
      {status === "HOH" &&
        visible.includes("spouseDeathYear") &&
        state.maritalSituation === "WIDOWED" &&
        state.spouseDeathYear === "2025" && (
          <p className="tax-topic-notice">
            A spouse’s death during 2025 does not by itself establish Head of
            Household status.{" "}
            {builderMode
              ? "Review the married filing options for the year of death and any applicable considered-unmarried exception."
              : "Review the year-of-death married filing rules and any applicable considered-unmarried exception."}
          </p>
        )}
      {status === "QSS" &&
        visible.includes("jointReturnEligibleInDeathYear") && (
          <fieldset className="filing-interview-group">
            <legend>After the death of a spouse</legend>
            <p>
              {visible.includes("spouseDeathYear")
                ? "For tax year 2025, review a spouse’s death in 2023 or 2024 and no remarriage by the end of 2025."
                : "Review whether a joint return was permitted in the year the spouse died."}
            </p>
            <div className="tax-topic-fields">
              {field(
                "jointReturnEligibleInDeathYear",
                yesNo,
                "Eligibility in the year of death matters; actually filing jointly that year is not required.",
              )}
            </div>
          </fieldset>
        )}
      {householdFields.some((name) => visible.includes(name)) && (
        <fieldset className="filing-interview-group">
          <legend>
            {status === "QSS"
              ? "Your home and qualifying child"
              : "Your home and qualifying person"}
          </legend>
          <p>
            {status === "HOH" &&
            visible.includes("qualifyingPersonRelationship") &&
            state.qualifyingPersonRelationship === "PARENT"
              ? "Review the cost of maintaining the parent’s main home for the entire year, including any applicable death exception. The parent does not have to live with the taxpayer. Dependency based solely on a multiple-support agreement does not establish Head of Household eligibility."
              : requestedFields &&
                  !visible.some((name) => name.startsWith("qualifyingPerson"))
                ? "Use the records of annual household costs and the taxpayer’s payments."
                : requestedFields &&
                    !visible.includes("paidMoreThanHalfHomeCosts")
                  ? "Use the person’s details from Dependents. Selecting a person here does not establish dependency eligibility."
                  : "Use the household cost records and the person’s details from Dependents. Review eligibility to claim the person, not merely whether the claim was entered. Selecting a person here does not establish dependency eligibility."}
          </p>
          <div className="tax-topic-fields">
            {field(
              "paidMoreThanHalfHomeCosts",
              yesNo,
              "Compare the taxpayer’s share with total qualifying annual costs. Exactly half is not more than half.",
            )}
            {field("qualifyingPersonName")}
            {field("qualifyingPersonRelationship", [
              ["CHILD", "Son, daughter, adopted child, or stepchild"],
              ["FOSTER_CHILD", "Foster child"],
              ["PARENT", "Parent"],
              ["OTHER_RELATIVE", "Another relative"],
              ["UNRELATED", "Unrelated person"],
              ["UNSURE", "Not sure"],
            ])}
            {field("qualifyingPersonResidence", [
              ["ALL_YEAR", "All year, including permitted temporary absences"],
              ["MORE_THAN_HALF", "More than half the year, but not all year"],
              ["HALF_OR_LESS", "Half the year or less"],
              [
                "EXCEPTION_REVIEW",
                "Birth, death, placement, or another exception — review needed",
              ],
              ["UNSURE", "Not sure"],
            ])}
            {field("qualifyingPersonClaim", [
              ["CLAIMED", "Claimed as a dependent on this return"],
              [
                "CUSTODIAL_PARENT_RELEASE",
                "Custodial parent released the dependency claim",
              ],
              [
                "EXCEPTION_REVIEW",
                "Dependency exception or special arrangement — review needed",
              ],
              ["NOT_CLAIMED", "Not claimed; no exception identified"],
              ["UNSURE", "Not sure"],
            ])}
          </div>
          {((visible.includes("qualifyingPersonClaim") &&
            state.qualifyingPersonClaim === "EXCEPTION_REVIEW") ||
            (visible.includes("qualifyingPersonResidence") &&
              state.qualifyingPersonResidence === "EXCEPTION_REVIEW")) && (
            <p className="tax-topic-notice">
              Keep the supporting records for the specific exception. This
              answer flags a review; it does not automatically establish that
              the person qualifies.
            </p>
          )}
          {status === "QSS" &&
            visible.includes("qualifyingPersonClaim") &&
            state.qualifyingPersonClaim === "EXCEPTION_REVIEW" && (
              <p className="tax-topic-notice">
                For Qualifying Surviving Spouse, review the specific
                child-income, child-joint-return, or taxpayer-claimability
                exceptions. Other dependency exceptions cannot be assumed to
                apply.
              </p>
            )}
        </fieldset>
      )}
      {status === "SINGLE" &&
        visible.includes("spouseDeathYear") &&
        state.maritalSituation === "WIDOWED" &&
        ["2023", "2024"].includes(state.spouseDeathYear ?? "") && (
          <p className="tax-topic-notice">
            The year of death may allow qualifying surviving spouse status if
            the other requirements are met. Review the household details before
            finalizing the filing status.
          </p>
        )}
      {realStatus &&
        showHouseholdSummary &&
        (!requestedFields ||
          householdFields.some((name) => visible.includes(name))) && (
          <div className="tax-topic-notice">
            <strong>Household information</strong>
            <p>{filingHouseholdSummary(selected)}</p>
            <button type="button" className="btn" onClick={onDependents}>
              Review Dependents
            </button>
          </div>
        )}
      {priorConflicts.length > 0 && (
        <div className="tax-topic-alert" role="alert">
          <strong>Review earlier filing-status information</strong>
          <ul>
            {priorConflicts.map((c, index) => (
              <li key={index}>{c.message}</li>
            ))}
          </ul>
          {!unseenConflicts && (
            <p>
              Earlier information has not been overwritten. Confirm only after
              reviewing the taxpayer’s records.
            </p>
          )}
          {unseenConflicts ? (
            <p>
              Review earlier information in the relevant topic. These answers
              cannot replace information that is not shown on this page.
            </p>
          ) : (
            <button
              type="button"
              className="btn"
              disabled={disabled}
              onClick={() =>
                onChange({
                  ...state,
                  version: 2,
                  reviewedPrevious: filingReviewToken(
                    previousRaw,
                    selected,
                    state,
                  ),
                })
              }
            >
              Use these answers after reviewing earlier information
            </button>
          )}
        </div>
      )}
      {!builderMode && (
        <p className="tax-topic-help">
          All five filing statuses can be recorded in this interview. Saving
          records draft information; it does not calculate tax, determine
          eligibility, or approve a return.
        </p>
      )}
    </section>
  );
}
